import { defineStore } from 'pinia'
import { computed, reactive, watch } from 'vue'
import ShoppingCart from './classes/ShoppingCart'
import PrintCloud from './classes/PrintCloud'
import CloudBasket from './classes/CloudBasket'
import { ARTICLE_TYPE_PRINTABLE, PRINT_CLOUD_STATUS_OPEN } from './constants'
import { getAvailability, getDocuments, getExhibitionInformation } from './services/api'
import { getActivePriceLevel } from './utils/helper'
import { 
  calculateRentCost,
  calculateBoothCost,
  calculateArticleCost,
  calculatePrintArticleCost,
  calculateAdditionalCost,
  calculatePriceLevelCost,
  calculateTotal,
  calculateTotalGross
} from './utils/basket'
import { debounce } from 'lodash'

/**
 * Version counter to reset basket and shopping cart in locale storage,
 * if changes were made in the structure of those two objects
 * 
 * @type {Integer}
 */
const STORE_VERSION = 10

export const useMainStore = defineStore('main', () => {
  const settings = reactive({
    /** @type {String} */
    locale: 'de',
    basket: null,
    /**
     * @type {ShoppingCart}
     */
    shopping_cart: new ShoppingCart({}),
    state: {
      version: STORE_VERSION,
      last_update: getTimeInSeconds()
    }
  })
  const tmp = reactive({
    cloud: new PrintCloud({ uuid: '', status: PRINT_CLOUD_STATUS_OPEN}),
    is_exhibition_closed: false,
    days_to_go: 0,
    print_articles: [],
    booth_articles: [],
    booth: null,
    exhibition_slug: null,
    job: null,
    sent_configuration_ids: [],
    refinements: [],
    booth_draft: null,
    printing_information: [],
    characters_inclusive: 0,
    early_bird_mode: false,
    late_delivery: null,
    disabled_booth_refinement_ids: [],
    booth_package_documents: [],
    current_tab: 'key_data',
    net_value_only: false,
    selected_booth: null,
    countries: [],
    categories: [],
    instructions: [],
  })

  // TODO: merge availability in exhibition
  const availability = reactive({
    available_article_ids: [],
    bookable_article_ids: [],
    disabled_booth_refinement_ids: [],
  })

  const exhibition = reactive({
    price_levels: [],
    cost_booth_by_type: [],
    costs: [],
  })

  const activeTabs = reactive({
    key_data: true,
    booths: false,
    booth_setup: false,
    articles: false,
    prints: false,
    summary: false,
    submit: false,
  })
  // TODO: move checks to store
  const checklist = reactive({
    minimum_checkout: false
  })

  const costs = reactive({
    squareMeter: 0,
		exhibitionRentCost: 0,
		exhibitionAdditionalCost: 0,
		additionalCosts: [],
		boothCost: 0,
		labelCost: 0,
		articlesCost: 0,
		isLateDeliverySurchargeActive: false,
		lateDeliveryCost: 0,
		priceLevelCost: 0,
		priceLevelId: null,
		total: 0,
		total_gross: 0
  })

  // print is a persisted storage for print cloud
  const print = reactive({
    /** @type {CloudBasket} */
    basket: new CloudBasket({ id: "cloudBasked" })
  })

  const settingsInStorage = localStorage.getItem('settings')
  if (settingsInStorage) {
    const { locale, basket, shopping_cart, state } = JSON.parse(settingsInStorage)
    /**
     *  @type {String}
     */
    settings.locale = locale

    if (isStorageInvalid(state)) {
      settings.basket = null
      /** @type {ShoppingCart} */
      settings.shopping_cart = new ShoppingCart({})
      settings.state = {
        version: STORE_VERSION,
        last_update: getTimeInSeconds()
      }
    } else {
      settings.basket = basket
      /** @type {ShoppingCart} */
      settings.shopping_cart = new ShoppingCart(shopping_cart)
      settings.state.last_update = getTimeInSeconds()
    }
  }
  const printInStorage = localStorage.getItem('print')
  if (printInStorage) {
    
    const { basket } = JSON.parse(printInStorage)
    /** @type {CloudBasket} */
    print.basket = new CloudBasket(basket)
  }

  const inEnglish = computed(() => settings.locale === 'en')

  const activePriceLevel = computed(() => {
    if (exhibition.price_levels.length === 0) {
      null
    }

    return getActivePriceLevel(exhibition.price_levels)
  })

  const additionalCosts = computed(() => {
    return exhibition.costs.filter(item => {
      if (settings.basket.boothType != null 
        && settings.basket.boothType.type != item.booth
        && item.booth != 'all') {
        return false
      }
      item.displayName = (inEnglish.value) ? item.name_en : item.name
  
      item.amount = 0
      const price = parseFloat(item.net)
      if (item.type == 'fix') {
        item.amount = price
      } else if (item.type == 'per_sqm') {
        item.amount = price * settings.shopping_cart.getSquareMeter()
      }
  
      return true
    })
  })

  watch(() => settings, (state) => {
    storeSettings(state)
  }, { deep: true })

  watch(() => print, (state) => {
    localStorage.setItem('print', JSON.stringify(state))
  }, { deep: true })

  // watch(() => settings.basket, () => {
  //   updateComputedPrices()
  // }, { deep: true })

  watch(() => settings.shopping_cart, () => {
    updateComputedPrices()
    updateActiveTabs()
    updateChecklist()
  }, { deep: true })

  watch(() => activePriceLevel.value, () => {
    updateComputedPrices()
  })

  watch(() => tmp.selected_booth, () => {
    updateActiveTabs()
  })

  const storeSettings = debounce((state) => {
    localStorage.setItem('settings', JSON.stringify(state))
  }, 500)

  const fetchAvailability = async () => {
    await getAvailability(tmp.exhibition_slug)
      .then(response => {
        availability.available_article_ids = response.available_article_ids
        availability.bookable_article_ids = response.bookable_article_ids
        availability.disabled_booth_refinement_ids = response.disabled_booth_refinement_ids
      })
  }

  const fetchExhibitionInformation = async () => {
    await getExhibitionInformation(tmp.exhibition_slug)
      .then(response => {
        exhibition.price_levels = response.price_levels
        exhibition.costs = response.costs
      })
  }
  const fetchDocuments = async () => {
    await getDocuments(tmp.exhibition_slug)
      .then(response => {
        tmp.printing_information = response.printing_information
        tmp.instructions = response.instructions
      })
  }

  const updateCurrentTab = (tab) => {
    tmp.current_tab = tab
  }
  
  const updateChecklist = () => {
    checklist.minimum_checkout = (
      settings.shopping_cart.booth !== null
      || settings.shopping_cart.items.length > 0
    )
  }

  const updateComputedPrices = debounce(() => {
    if (
      settings.basket === undefined
      || settings.basket === null
    ) {
      return
    }
    costs.squareMeter = settings.shopping_cart.getSquareMeter()
    costs.exhibitionRentCost = calculateRentCost(
      settings.basket.boothType,
      [],
      costs.squareMeter
    )
    costs.boothCost = settings.shopping_cart.costs.booth
    costs.articlesCost = calculateArticleCost(
        settings.shopping_cart.items,
        settings.shopping_cart.getSquareMeter(),
        tmp.net_value_only
      )
		  + calculatePrintArticleCost(
        settings.shopping_cart.print_items,
        settings.shopping_cart.getSquareMeter(),
        settings.shopping_cart,
        tmp.net_value_only,
        tmp.characters_inclusive
      )
    // costs.additionalCosts is the only dependency not covered by shopping cart
    costs.additionalCosts = _.cloneDeep(additionalCosts.value)
    costs.exhibitionAdditionalCost = calculateAdditionalCost(costs.additionalCosts)
    costs.priceLevelCost = (activePriceLevel.value !== null)
      ? calculatePriceLevelCost(activePriceLevel.value, costs.boothCost, costs.articlesCost)
      : 0
    costs.total = settings.basket.total = calculateTotal(costs)
    costs.total_gross = settings.basket.total_gross = calculateTotalGross(
      costs.total,
      (tmp.net_value_only) ? 0 : 19
    )
  }, 100)

  const updateActiveTabs = () => {
    if (settings.basket === null) {
      return
    }
    const keyDataSelected = (settings.basket.width > 0 && settings.basket.depth > 0 && settings.basket.boothType !== null)
    activeTabs.key_data = (!settings.basket.restricted && !tmp.is_exhibition_closed)
    activeTabs.booths = (!settings.basket.restricted && keyDataSelected && !tmp.is_exhibition_closed)
    activeTabs.booth_setup = (
      !settings.basket.restricted
      && keyDataSelected
      && (settings.basket.selectedBooth !== null || tmp.selected_booth !== null)
      && !tmp.is_exhibition_closed
    )
    activeTabs.articles = (keyDataSelected && !tmp.is_exhibition_closed)
    const printableItemsCount = settings.shopping_cart.items.filter((item) => item.type === ARTICLE_TYPE_PRINTABLE).length
    activeTabs.prints = (keyDataSelected && printableItemsCount > 0 && !tmp.is_exhibition_closed)
    activeTabs.summary = (keyDataSelected && !tmp.is_exhibition_closed && checklist.minimum_checkout)
    activeTabs.submit = (keyDataSelected && !tmp.is_exhibition_closed && checklist.minimum_checkout)
  }

  return {
    settings,
    tmp,
    print,
    availability,
    exhibition,
    fetchAvailability,
    fetchExhibitionInformation,
    fetchDocuments,
    inEnglish,
    activePriceLevel,
    updateCurrentTab,
    activeTabs,
    updateActiveTabs,
    checklist,
    updateChecklist,
    costs
  }
})

const isStorageInvalid = (state) => {
  if (state === undefined || state === null || state.version !== STORE_VERSION) {
    return true
  }

  const now = getTimeInSeconds()
  // check if storage is expired after 48 hours
  if ((now - state.last_update) > 172800) {
    return true
  }

  return false
}

const getTimeInSeconds = () => {
  return Date.now() / 1000
}